// ─────────────────────────────────────────────────────────────────────────────
// TWO DESIGN VERSIONS — STORY 1 LEFT, STORY 2 RIGHT. CONSOLIDATED 23 AUG 2026.
//
// The board carried six: a tab switcher, then six shrunk thumbnails in one
// row, then six full-size sections — three shapes in one session, and each
// time Erez looked at the actual result and corrected the shape, not the
// content. This pass corrects the CONTENT: having finally seen all six full
// size, side by side, he reviewed them and cut four.
//
// HIS VERDICT, VERBATIM WHERE IT MATTERS:
//   "Let's clean everything and keep just Story 1, Story 2 — make Story 1 on
//    the left and Story 2 on the right, delete the other ones."
//
// WHAT WENT AND WHY (his words, so the reasoning survives the deletion even
// though the sections do not — Tuesday is two, maybe three concepts, not six):
//   · v3 ("Centred thread, half panel") — the table inside the chat is bad,
//     the popups are bad. ⭐ BUT REMEMBER: the small agent panel on the side
//     that expands toward full screen was genuinely liked. If a future
//     version resurrects that interaction, this is where it came from —
//     the component is still workspace-ux-3.tsx (OutformWorkspaceUX3),
//     untouched, just no longer mounted on this board.
//   · v2 ("Their workbook, inside the system") — still the OLD table, not
//     the Univer one Story 1/Story 2 now carry. Superseded, not salvageable.
//   · v1 ("The workspace") — superseded by a better version already on the
//     board (Story 1/Story 2 both grew out of it).
//   · v0 ("The first prototype") — the oldest attempt, superseded.
//   None of the four component files were deleted (workspace-ux.tsx,
//   workspace-ux-2.tsx, workspace-ux-3.tsx, proposal-ux.tsx's
//   OutformWorkbench) — board law is nothing comes off a board without
//   Erez's word, and he gave it for the BOARD section, not for purging the
//   code. Git history and the working tree both still have them; this file
//   just stopped importing three of the four (OutformWorkbench serves a
//   different section, "The product", via OutformProductUX in the same
//   proposal-ux.tsx file — that import lives in OutformBoardPage, untouched).
//
// STILL OPEN, RAISED BACK TO ERĖZ RATHER THAN GUESSED AT: he flagged "popups
// that are popping up are too bad" while reviewing — unclear whether that
// was about v3 (now deleted, so moot) or about Story 1/Story 2 (kept, so it
// matters). Ask before touching either kept story's popups.
// ─────────────────────────────────────────────────────────────────────────────

import { lazy, Suspense } from "react";
import "./design-versions.css";

const V5 = lazy(() => import("./workspace-ux-4").then((m) => ({ default: m.OutformWorkspaceUX4 })));
const V6 = lazy(() => import("./workspace-ux-5").then((m) => ({ default: m.OutformWorkspaceUX5 })));
const V7 = lazy(() => import("./workspace-ux-6").then((m) => ({ default: m.OutformWorkspaceUX6 })));

// Story 1 first — OutformBoardPage renders this array in order, left to
// right, and Erez's order is "Story 1 on the left, Story 2 on the right".
export const OUTFORM_DESIGN_VERSIONS = [
  {
    key: "v5",
    n: "Story 1",
    title: "A ballpark from a drawing",
    sub: "13 narrated steps · it shows its working · the real spreadsheet in the panel",
    Comp: V5,
  },
  {
    key: "v6",
    n: "Story 2",
    title: "The client pushes back",
    sub: "14 narrated steps · home page · the Excel opens beside the chat and grows",
    Comp: V6,
  },
  {
    // Added 27 Aug 2026, after the Data Pilot demo was read frame by frame
    // (kb-doc/consultancy/outform/competitor-demo). Story 1 and 2 both send
    // PROSE; the whole point of this one is that the agent sends OBJECTS you
    // can pick, override and argue with — which is the interaction the client
    // actually responded to in the competitor's demo.
    key: "v7",
    n: "Story 3",
    title: "The agent sends objects",
    sub: "13 steps · open a comparable and argue with it · evidence per cost category · shapes.co chrome · and it refuses the number it cannot source",
    Comp: V7,
  },
] as const;

/** One version, full size. OutformBoardPage mounts two of these, one per
 *  SectionBox, so each lives in its own React-Flow child. */
export function OutformDesignVersionScreen({ versionKey, width = 1710 }: { versionKey: string; width?: number }) {
  const v = OUTFORM_DESIGN_VERSIONS.find((x) => x.key === versionKey);
  if (!v) return null;

  return (
    <div className="nowheel nodrag nopan" style={{ width }}>
      <div className="of-dv-sub">{v.sub}</div>
      <Suspense fallback={<div className="of-dv-wait" style={{ width }}>loading {v.title.toLowerCase()}…</div>}>
        <v.Comp width={width} />
      </Suspense>
    </div>
  );
}
